import { useCognitoAuth } from '@/contexts/cognitoAuthContext'
import { DashboardExperiments } from '@/experiments'
import { useFlag } from '@/hooks/useFlag'
import { useGetAccountInfo } from '@/queries/account/useGetAccountInfo'
import { useGetSelfServiceInfo } from '@/queries/premium/useGetSelfServiceInfo'
import { CoreLevelFeatureNames } from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import { useCorePermissions } from '@/stores/permissions/permissions'
import { useMemo } from 'react'

export const useSelfServeAndPermissions = () => {
  const { accountId, isAuthenticated } = useCognitoAuth()
  const { data: accountInfo, isLoading: isAccountInfoLoading } = useGetAccountInfo({ accountId })
  const { data: selfServiceInfo, isLoading: isSelfServiceInfoLoading } = useGetSelfServiceInfo({
    enabled: isAuthenticated && accountInfo?.accountType === 'SELF_SERVE',
  })

  const billingAndPaymentsPermission = useCorePermissions(CoreLevelFeatureNames.billingAndPayments)
  const upgradePackagePermission = useCorePermissions(CoreLevelFeatureNames.upgradePackage)

  const showBillingAndPaymentsPage = useFlag(DashboardExperiments.billingAndPaymentsPage)
  const showUpgradePackagePage = useFlag(DashboardExperiments.upgradePackagePage)

  const shouldShowBillingAndPayments = useMemo(() => {
    const hasPermission = !!billingAndPaymentsPermission?.view
    return (
      showBillingAndPaymentsPage || (hasPermission && accountInfo?.accountType === 'SELF_SERVE')
    )
  }, [billingAndPaymentsPermission, showBillingAndPaymentsPage, accountInfo])

  const canDoUpgrade = useMemo(() => {
    if (isSelfServiceInfoLoading || isAccountInfoLoading) return true

    const hasPermission = !!upgradePackagePermission?.view
    const isSelfServe = accountInfo?.accountType === 'SELF_SERVE'
    const hasUpgrade = !!selfServiceInfo?.hasUpgrade

    return hasUpgrade && hasPermission && isSelfServe && showUpgradePackagePage
  }, [
    isSelfServiceInfoLoading,
    isAccountInfoLoading,
    upgradePackagePermission?.view,
    accountInfo?.accountType,
    selfServiceInfo?.hasUpgrade,
    showUpgradePackagePage,
  ])

  const shouldDoUpgrade = useMemo(() => {
    return selfServiceInfo?.hasChosenFirstPackage === false
  }, [selfServiceInfo?.hasChosenFirstPackage])

  const shouldDoOnboarding = useMemo(() => {
    return selfServiceInfo?.storesAmount === 0
  }, [selfServiceInfo?.storesAmount])

  return {
    shouldShowBillingAndPayments,
    canDoUpgrade,
    isAccountInfoLoading,
    shouldDoUpgrade,
    isSelfServiceInfoLoading,
    shouldDoOnboarding,
  }
}
